import React, {  CSSProperties, useMemo } from 'react';
import classNames from 'classnames';
import get from 'lodash/get';
import { AnimType,  BaseShapeDataModel,  ENTR_ID_MAP,  EXIT_ID_MAP,  NodeType,  PresetSubtype } from '@tencent/slide-sdk-model';
import AnimTypeSelect from './anim-type-select';
import i18n from '@i18next/i18n';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { ANIM_RECENT_ID, ANIM_REPORT, REPORT_MAP } from '../../constants';
import { IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import AnimItemProperty from './anim-item-property';
import Tooltip from '@tencent/dui/lib/components/Tooltip';
import Select from '@tencent/dui/lib/components/Select';
import AnimItemEffect from './anim-item-effect';
import { AnimationHistory } from '../../animationHistory';
import { iconTypeMap, ShapeTypeMap } from '../constants';
import { PresetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constants';
import { isPathAnim } from '../utils';
import { ANIM_ITEM_TEXT, SHAPE_LIST } from '@src/js/js-keyframe-animation/style-converter/constants';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

import Style from '../index.less';

type AnimModifyParams = {
    delay?: number,
    duration?: number,
    clickSpid?: string | string[],
    animType?: string | number,
    subType?: number,
    nodeType?: number,
    by?: TModifyBy,
    opacity?: string
};

export type AnimItemBoardProps = {
    visible?: boolean;
    animType: AnimType;
    subType: string | number;
    sTypes: PresetSubtype[];
    triggerSelectOptions: {value: string, name: string}[];
    isShown?: boolean
    contentHeight: number
    animationModel: IAnimModel;
    selectionService?: ISelectionService,
    onAnimEnd?: () => void;
    onModify?: (params: AnimModifyParams) => void
    onSlideValueEnter?: () => void;
    onSlideValueLeave?: () => void;
};

type TModifyBy = string | number | { x: number; y: number; };

const FLOAT_IN_MAP = {
    [PresetSubtype.Top]: AnimType.FloatDownIn,
    [PresetSubtype.Bottom]: AnimType.FloatUpIn,
};
const FLOAT_OUT_MAP = {
    [PresetSubtype.Top]: AnimType.FloatDownOut,
    [PresetSubtype.Bottom]: AnimType.FloatUpOut,
};
const REPORT_FROM_SLIDER = 1;
const REPORT_FROM_INPUT = 2;

const triggerStyle: CSSProperties = {
    fontSize: 12,
    cursor: 'default',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

const AnimItemBoard = (props: AnimItemBoardProps) => {
    const {
        visible,
        animType,
        subType,
        triggerSelectOptions,
        sTypes,
        isShown,
        contentHeight,
        animationModel,
        selectionService,
        onModify,
        onAnimEnd,
        onSlideValueEnter,
        onSlideValueLeave,
    } = props;

    const animHistory = AnimationHistory.getInstance();

    const emphPropertyValue = animationModel.props.opacityValue || 0;
    const delayValue = parseInt(`${animationModel.props.delay}`, 10) / 1000;
    const durationValue = parseInt(`${animationModel.props.duration}`, 10) / 1000;
    const nodeTypeValue = animationModel?.props?.nodeType;

    const nodeTypeOptions = [
        { key: 'clickEffect', value: NodeType.CLICK_EFFECT, label: i18n.t('单击时') },
        { key: 'withEffect', value: NodeType.WITH_EFFECT, label: i18n.t('与上一个同时') },
        { key: 'afterEffect', value: NodeType.AFTER_EFFECT, label: i18n.t('在上一个之后') },
    ];
    const hideDuration = [AnimType.Appear, AnimType.Disappear].includes(animType);

    const handleAnimationEnd = () => {
        if (!isShown) {
            onAnimEnd?.();
        }
    };

    const tdwReport = (report: Record<string, unknown>) => {
        const { elementModel } = animationModel;
        tdw({ ...ANIM_REPORT, ver7: elementModel.type, ...report });
    };

    // 修改动画类型
    const handleAnimationTypeChange = (value: string, categoryId?: string) => {
        const animationType = iconTypeMap[value] ? iconTypeMap[value] : value;
        // 保存到 localstorage 作为最近使用
        // 历史记录中形状统一处理成圆形
        animHistory.setAnimationHistory(ShapeTypeMap[animationType] || animationType);
        onModify?.({ animType: animationType });

        tdwReport({
            ...REPORT_MAP.animType,
            ver5: animationModel?.props?.presetClass,
            ver6: animType,
            ver8: categoryId === ANIM_RECENT_ID,
        });
    };

    // 强调动画透明度属性修改
    const handleEmphOpacityChange = (value, changing) => {
        const valueNum = (isNaN(parseFloat(value)) ? 0 : parseFloat(value));
        const opacity = Math.round(100 - valueNum) / 100;
        onModify?.({ opacity: opacity.toString() });
        tdwReport({
            ...REPORT_MAP.setProperty,
            ver5: opacity,
            ver6: typeof changing !== 'undefined'
                ? REPORT_FROM_SLIDER : REPORT_FROM_INPUT,
        });
    };

    // 动画方向修改
    const handleSubTypeChange = (v: string) => {
        const value = parseInt(v, 10);
        const animTypeMap = {
            [AnimType.FloatDownIn]: FLOAT_IN_MAP,
            [AnimType.FloatUpIn]: FLOAT_IN_MAP,
            [AnimType.FloatDownOut]: FLOAT_OUT_MAP,
            [AnimType.FloatUpOut]: FLOAT_OUT_MAP,
        };
        if (animTypeMap[animType]) {
            const newAnimType = get(animTypeMap[animType], value, value);
            onModify?.({ animType: newAnimType });
            tdwReport({ ...REPORT_MAP.subType, ver5: newAnimType, ver6: '' });
            return;
        }
        if (animType === AnimType.Scale || animType === AnimType.Rotate) {
            const map: Record<number, TModifyBy> = {
                [PresetSubtype.LeftRight]: { x: 1.5, y: 1 },
                [PresetSubtype.TopBottom]: { x: 1, y: 1.5 },
                [PresetSubtype.Total]: { x: 1.5, y: 1.5 },
                [PresetSubtype.Clockwise]: 360,
                [PresetSubtype.Counterclockwise]: -360,
            };
            const by = get(map, parseInt(String(value), 10), '');
            onModify?.({ by });
            tdwReport({ ...REPORT_MAP.subType, ver5: by, ver6: '' });
            return;
        }
        const subType = parseInt(String(value), 10);
        onModify?.({ subType });
        tdwReport({ ...REPORT_MAP.subType, ver5: subType, ver6: '' });
    };

    // 形状动画形状类型修改
    const handleShapeChange = (shapeId: number) => {
        // 原始是形状的退出动画，选择不同形状后不改变动画类型
        if (animationModel?.props?.presetClass === PresetClassTypes.EXIT) {
            handleAnimationTypeChange(EXIT_ID_MAP[shapeId]);
        } else {
            handleAnimationTypeChange(ENTR_ID_MAP[shapeId]);
        }
    };

    // 开始类型修改
    const handleNodeTypeChange = (value: number) => {
        onModify?.({ nodeType: value });
        tdwReport({ ...REPORT_MAP.nodeType, ver5: value, ver6: '' });
    };

    // 延迟属性修改
    const handleDelayChange = (value: string, changing?: boolean) => {
        const delay = (isNaN(parseFloat(value)) ? 0 : parseFloat(value)) * 1000;
        onModify?.({ delay });
        tdwReport({
            ...REPORT_MAP.delay,
            ver5: delayValue,
            ver6: typeof changing !== 'undefined'
                ? REPORT_FROM_SLIDER : REPORT_FROM_INPUT,
        });
    };

    // 持续时间属性修改
    const handleDurationChange = (value: string, changing?: boolean) => {
        let duration = (isNaN(parseFloat(value)) ? 0 : parseFloat(value)) * 1000;
        duration = Math.max(10, duration);
        onModify?.({ duration });
        tdwReport({
            ...REPORT_MAP.duration,
            ver5: duration,
            ver6: typeof changing !== 'undefined'
                ? REPORT_FROM_SLIDER : REPORT_FROM_INPUT,
        });
    };

    const handleTriggerOptionMouseEnter = (value: string) => {
        if (!selectionService) {
            return;
        }
        const { currentSlide } = selectionService;
        if (currentSlide) currentSlide.highlightModelId = value;
    };

    const handleTriggerOptionMouseLeave = () => {
        if (!selectionService) {
            return;
        }
        const { currentSlide } = selectionService;
        if (currentSlide) currentSlide.highlightModelId = null;
    };

    const renderTriggerOption = (ops) => {
        const { value, label, style, key } = ops;
        const disableTooltip = !label || label.length < 8;
        return <div key={key}
            onMouseEnter={() => handleTriggerOptionMouseEnter(value)}
            onMouseLeave={handleTriggerOptionMouseLeave}>
            <Select.Option style={{ display: 'block' }}
                key={`${key}-${value}`}
                value={value}>
                <Tooltip
                    title={label}
                    trigger="hover"
                    placement="bottom"
                    disabled={disableTooltip}
                    overlayHoverable={false}
                >
                    <span className="trigger-item-span" style={style}>
                        {label}
                    </span>
                </Tooltip>
            </Select.Option>
        </div>;
    };

    const handleClickSpidChange = (clickSpid: string) => {
        handleTriggerOptionMouseLeave();
        onModify?.({ clickSpid });
        tdwReport({
            ...REPORT_MAP.clickSpid,
            ver5: clickSpid,
            ver6: '',
        });
    };

    const isShapeAnim = useMemo<boolean>(() => [
        AnimType.EntrShapeCircle,
        AnimType.ExitShapeCircle,
        AnimType.EntrShapeBox,
        AnimType.ExitShapeBox,
        AnimType.EntrShapeDiamond,
        AnimType.ExitShapeDiamond,
        AnimType.EntrShapePlus,
        AnimType.ExitShapePlus,
    ].includes(animType), [animType]);

    const pageDataModel = useMemo(() => {
        if (animationModel.elementModel?.parent?.type === UIModelType.TABLE) {
            return (animationModel.elementModel.parent.sdkData as BaseShapeDataModel)?.getPage?.();
        }
        return animationModel.elementModel?.sdkData?.getPage?.();
    }, [animationModel]);

    const shapePropertyOptions = useMemo(() => {
        const options = sTypes.map((st) => {
            const key = `shape-dir-${st}`;
            const value = `${st}`;
            const label = ANIM_ITEM_TEXT[animType + st] || '';
            return { key, value, label };
        });

        SHAPE_LIST.forEach((shape) => {
            const entrVal = {
                key: `shape-entr-${shape.id}`,
                value: ENTR_ID_MAP[shape.id],
                label: shape.label,
            };
            const exitVal = {
                key: `shape-exit-${shape.id}`,
                value: EXIT_ID_MAP[shape.id],
                label: shape.label,
            };
            options.push(entrVal, exitVal);
        });
        return options;
    }, [sTypes, animType]);

    const disabled = useMemo(() => isPathAnim(animType), [animType]);

    const triggerOptions = useMemo(() => triggerSelectOptions.map(({ name, value }) => ({
        key: value,
        value,
        style: triggerStyle,
        label: name,
    })), [triggerSelectOptions]);

    const isColorEffect = useMemo(() => [
        AnimType.FillColor,
        AnimType.FontColor,
        AnimType.LineColor,
        AnimType.BrushColor,
        AnimType.ObjectColor,
        AnimType.ColorPulse,
        AnimType.GrowWithColor,
    ].includes(animType), [animType]);

    return (
        <div
            className={classNames(
                Style['content-wrapper'],
                {
                    [Style['content-wrapper-visible']]: visible,
                    [Style['content-wrapper-hidden']]: !visible,
                },
            )}
        >
            <div className={classNames(Style.content, {
                [Style['content-show']]: isShown,
                [Style['content-hide']]: !isShown,
            })}
            style={{ marginTop: isShown ? 0 : -contentHeight }}
            onTransitionEnd={handleAnimationEnd}
            >
                {/* 动画类型 */}
                <AnimTypeSelect
                    animType={animType}
                    disabled={disabled}
                    onModify={handleAnimationTypeChange}
                />
                {/*  颜色动画属性 */}
                {
                    isColorEffect
                        ? <AnimItemProperty
                            title={i18n.t('属性')}
                            key={`color-${animType}`}
                            valueType='color'
                            colorPickerProps={{
                                onModify: onModify as Function,
                                pageDataModel,
                                choiceList: (animationModel?.sdkData as any).par.cTn.childTnLst.egTimeNodeChoice,
                            }}
                        />
                        : null
                }
                {/* 强调动画透明度属性 */}
                { animType === AnimType.EmphTransparency
                    ? <AnimItemProperty
                        title={i18n.t('属性')}
                        key={`emph-${animType}`}
                        valueType='numberSlideBar'
                        onPropertyChange={handleEmphOpacityChange}
                        numberSlidebarProps={{
                            value: emphPropertyValue,
                            onValueEnter: onSlideValueEnter,
                            onValueLeave: onSlideValueLeave,
                        }}
                    />
                    : null}
                {/* 动画方向或形状属性 */}
                {isShapeAnim
                    ? <AnimItemProperty
                        title={i18n.t('属性')}
                        key={`prop-${animType}`}
                        valueType='select'
                        hidden={!shapePropertyOptions.length}
                        selectProps={{
                            isMultiple: true,
                            value: [animType, subType],
                            listStyle: { padding: 0 },
                            options: shapePropertyOptions,
                            renderTag: tag => (
                                <span
                                    key={tag.value}
                                    className={Style['shape-tag']}
                                >
                                    {tag.children}
                                </span>
                            ),
                            renderDropdown: () =>
                                <AnimItemEffect
                                    animType={animType}
                                    subType={subType}
                                    sTypes={sTypes}
                                    onSubTypeChange={handleSubTypeChange}
                                    onShapeChange={handleShapeChange}
                                />,
                        }}
                    />
                    : <AnimItemProperty
                        key={`anim-item-dir-${animType}`}
                        title={i18n.t('方向')}
                        hidden={!sTypes.length}
                        valueType='select'
                        onPropertyChange={handleSubTypeChange}
                        selectProps={{
                            value: subType,
                            disabled,
                            options: sTypes.map((t) => {
                                const value = `${t}`;
                                const label = ANIM_ITEM_TEXT[animType + t] || '';
                                return { key: `dir-${t}`, label, value };
                            }),
                        }}
                    />}

                {/* 动画开始属性 */}
                <AnimItemProperty
                    key={`anim-item-start-${animType}`}
                    title={i18n.t('开始')}
                    valueType='select'
                    onPropertyChange={handleNodeTypeChange}
                    selectProps={{
                        disabled,
                        value: nodeTypeValue,
                        options: nodeTypeOptions,
                    }}
                />
                {/* 动画延时 */}
                <AnimItemProperty
                    key={`anim-item-delay-${animType}`}
                    title={i18n.t('延迟')}
                    valueType='numberSlideBar'
                    onPropertyChange={handleDelayChange}
                    numberSlidebarProps={{
                        disabled,
                        value: delayValue,
                        toFixed: 2,
                        slideBarStep: 0.5,
                        numberInputStep: 0.5,
                        lowerLimit: 0,
                        upperLimit: 60,
                        allowDecimal: true,
                        postFix: 's',
                        size: 'small',
                        type: 'delay',
                        onValueEnter: onSlideValueEnter,
                        onValueLeave: onSlideValueLeave,
                    }}
                />
                {/* { this.renderDelay() } */}
                {/* 持续时间 */}
                <AnimItemProperty
                    key={`anim-item-duration-${animType}`}
                    title={i18n.t('持续时间')}
                    hidden={hideDuration}
                    valueType='numberSlideBar'
                    onPropertyChange={handleDurationChange}
                    numberSlidebarProps={{
                        disabled,
                        value: durationValue,
                        toFixed: 2,
                        slideBarStep: 0.5,
                        numberInputStep: 0.5,
                        lowerLimit: 0,
                        upperLimit: 600,
                        sliderUpperLimit: 60,
                        allowDecimal: true,
                        postFix: 's',
                        size: 'small',
                        type: 'duration',
                        onValueEnter: onSlideValueEnter,
                        onValueLeave: onSlideValueLeave,
                    }}
                />
                {/* 触发器 */}
                <AnimItemProperty
                    title={i18n.t('触发器')}
                    valueType='select'
                    onPropertyChange={handleClickSpidChange}
                    selectProps={{
                        disabled,
                        value: animationModel.clickSpid,
                        options: triggerOptions,
                        listStyle: { width: '200px' },
                        dropdownMatchSelectWidth: false,
                        textStyle: {
                            ...triggerStyle,
                            lineHeight: '22px',
                            marginTop: '6px',
                        },
                        customOptionEl: renderTriggerOption,
                    }}
                />
            </div>
        </div>
    );
};

export default AnimItemBoard;
